import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, softwareAppSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, BarChart3Icon, ShoppingCartIcon, SparklesIcon, ChevronDownIcon } from 'lucide-react';
export function FlowBoostPage() {
  usePageSEO({
    canonical: '/flowboost',
    keywords: 'Shopify speed optimization, FlowBoost app, Shopify page speed, Core Web Vitals Shopify, fast Shopify store',
    ogType: 'product',
    jsonLd: [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }, {
      name: 'FlowBoost',
      path: '/flowboost'
    }]), softwareAppSchema({
      name: 'FlowBoost',
      description: 'FlowBoost by Design Flow Digitals is the ultimate Shopify speed optimization app. Boost Core Web Vitals, improve page load times, and increase conversions.',
      path: '/flowboost',
      category: 'BusinessApplication'
    })],
    title: 'FlowBoost | Design Flow Digitals',
    description: 'FlowBoost by Design Flow Digitals is the ultimate Shopify speed optimization app. Boost Core Web Vitals, improve page load times, and increase conversions.'
  });
  return <div className="w-full" data-id="element-965" data-mp="225sucvky6vms">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-966" data-mp="22513tz67jo3d">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-black pointer-events-none" data-id="element-967" data-mp="266366ovf8g9k" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-968" data-mp="2aywaxcqw3oln">
          <div data-id="element-969" data-mp="2kwai0lqxb11a">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-blue-500/30 bg-blue-500/10" data-id="element-970" data-mp="2kwu0c3khjrfu">
              <SparklesIcon className="w-4 h-4 text-blue-400" data-id="element-971" data-mp="2pw6msn7g2whw" />
              <span className="text-sm font-medium text-blue-300" data-id="element-972" data-mp="206q2pno1e4by">
                Shopify App
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-973" data-mp="1v9hh1d3oji5y">
              FlowBoost
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-10 leading-relaxed" data-id="element-974" data-mp="29ydp3a5qkv6k">
              Boost your Shopify store's conversion rate with intelligent upsell
              automation.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-975" data-mp="2iztybekter97">
              <button className="bg-white text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-200 transition-colors" data-id="element-976" data-mp="2dzfops3kf992">
                Start Free Trial
              </button>
              <button className="px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-977" data-mp="2nwapkhnb9lqi">
                View Demo
              </button>
            </div>
          </div>

          {/* Product Demo Mockup */}
          <div className="relative" data-id="element-978" data-mp="tl5n3gnynfcr">
            <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full" data-id="element-979" data-mp="355wefooryneu" />
            <div className="glass-card border-white/10 p-2 rounded-2xl relative z-10 shadow-2xl shadow-black/50" data-id="element-980" data-mp="3a5r5bs8jvczw">
              <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-981" data-mp="2vh4qrsqqyp9q">
                {/* Fake App UI */}
                <div className="h-12 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-982" data-mp="30dv3c63cf2qe">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-983" data-mp="2mede5pm4xngi" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-984" data-mp="2qibg082uhcd9" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-985" data-mp="2ci0jaoeut3k6" />
                </div>
                <div className="p-8" data-id="element-986" data-mp="2hg235bk3t6wp">
                  <div className="flex justify-between items-center mb-8" data-id="element-987" data-mp="2hh3rghq4rpwd">
                    <h3 className="text-xl font-bold text-white" data-id="element-988" data-mp="apujpwr7bwmc">
                      Upsell Performance
                    </h3>
                    <span className="text-green-400 bg-green-400/10 px-3 py-1 rounded-full text-sm" data-id="element-989" data-mp="fofkpyea2kbu">
                      +24.5%
                    </span>
                  </div>
                  <div className="h-40 bg-gradient-to-t from-blue-500/20 to-transparent border-b border-blue-500/50 relative mb-8" data-id="element-990" data-mp="26uk44xkz9ns4">
                    {/* Fake chart line */}
                    <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-991" data-mp="1xt3wgxljrzrl">
                      <path d="M0,100 L20,80 L40,90 L60,40 L80,50 L100,20" fill="none" stroke="#3b82f6" strokeWidth="2" data-id="element-992" data-mp="1o2iv52ehoh4z" />
                    </svg>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-993" data-mp="1a3j6vvuw249c">
                    <div className="bg-white/5 p-4 rounded-lg" data-id="element-994" data-mp="1a52njnkgihbn">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-995" data-mp="1557zrsvza7ut">
                        Extra Revenue
                      </p>
                      <p className="text-2xl font-bold text-white" data-id="element-996" data-mp="3gtcu9bda5t5e">$12,450</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg" data-id="element-997" data-mp="3lsfzavt9fzdd">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-998" data-mp="3qqrapaz7ehxp">
                        Conversion Rate
                      </p>
                      <p className="text-2xl font-bold text-white" data-id="element-999" data-mp="3uoxbm2dd23ap">4.8%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="section-padding bg-black" data-id="element-1000" data-mp="37170px7dr3o3">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1001" data-mp="36zm7qc0prssc">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1002" data-mp="3bsyushpqvriz">Key Features</h2>
          <p className="text-xl text-gray-400" data-id="element-1003" data-mp="2fasumn5lyvmz">
            Everything you need to maximize average order value.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1004" data-mp="2jabgpm961cre">
          {[{
          icon: <SparklesIcon className="w-8 h-8 text-blue-400" data-id="element-1005" data-mp="3ml7azemxrsnd" />,
          title: 'AI Product Recommendations',
          desc: 'Machine learning algorithms suggest the perfect products based on cart contents.'
        }, {
          icon: <ShoppingCartIcon className="w-8 h-8 text-blue-400" data-id="element-1006" data-mp="3rcyw39nbp664" />,
          title: 'Smart Cart Upsells',
          desc: 'In-cart offers that convert without interrupting the checkout flow.'
        }, {
          icon: <BarChart3Icon className="w-8 h-8 text-blue-400" data-id="element-1007" data-mp="2yqe3k5v42o0z" />,
          title: 'Analytics Dashboard',
          desc: 'Track every extra dollar earned with detailed performance metrics.'
        }].map((feature, i) => <div key={i} className="glass-card p-8 border-white/10" data-id="element-1008" data-mp="ry3kdfb9868p" data-mpi={i}>
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-1009" data-mp="s0gcglaq7wzh">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-1010" data-mp="vf9gqfhg0sad">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1011" data-mp="10g5m6xhg0mhw">{feature.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding" data-id="element-1012" data-mp="qgy45wzrk3og">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1013" data-mp="qhq2hfkqwfif">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1014" data-mp="1e691f4hwnfxu">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1015" data-mp="1j653gy71kxar">
            Start free, upgrade when you grow.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1016" data-mp="14g7n34fctbw4">
          <div className="glass-card p-10 border-white/10" data-id="element-1017" data-mp="19f0ovzla3egg">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1018" data-mp="1x5mxbrz730qz">Basic</h3>
            <p className="text-gray-400 mb-6" data-id="element-1019" data-mp="1x4bupvhhjz1g">Perfect for new stores</p>
            <div className="mb-8" data-id="element-1020" data-mp="21x4z2i91l5d0">
              <span className="text-5xl font-bold text-white" data-id="element-1021" data-mp="21yg2ui2hmd44">$19</span>
              <span className="text-gray-400" data-id="element-1022" data-mp="1vomh4z6efzj">/mo</span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-1023" data-mp="3t8j8r6uvxvll">
              {['Up to 500 orders/mo', 'Basic recommendations', 'Standard support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-1024" data-mp="3k95yibyxt2zt" data-mpi={i}>
                  <CheckCircle2Icon className="w-5 h-5 text-blue-400" data-id="element-1025" data-mp="kthn20mbudtr" /> {f}
                </li>)}
            </ul>
            <button className="w-full py-4 rounded-lg font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1026" data-mp="gspezap6yez2">
              Start 14-Day Trial
            </button>
          </div>

          <div className="glass-card p-10 border-blue-500/30 bg-blue-500/5 relative overflow-hidden" data-id="element-1027" data-mp="6sosjfw0ziqn">
            <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-1028" data-mp="2wk3it9nv5k7y">
              POPULAR
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1029" data-mp="2hg0pq6uk2mvc">Pro</h3>
            <p className="text-gray-400 mb-6" data-id="element-1030" data-mp="2hdo3hhhrlqch">For growing businesses</p>
            <div className="mb-8" data-id="element-1031" data-mp="2mev8x9wfqdfz">
              <span className="text-5xl font-bold text-white" data-id="element-1032" data-mp="2rdokgws5vudi">$49</span>
              <span className="text-gray-400" data-id="element-1033" data-mp="2rblnttwi72zt">/mo</span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-1034" data-mp="2w56nrrhnq71n">
              {['Unlimited orders', 'AI-powered recommendations', 'A/B testing', 'Priority support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-1035" data-mp="3a46dz3ktitzx" data-mpi={i}>
                  <CheckCircle2Icon className="w-5 h-5 text-blue-400" data-id="element-1036" data-mp="3a23cnne7tefw" /> {f}
                </li>)}
            </ul>
            <button className="w-full py-4 rounded-lg font-bold bg-white text-black hover:bg-gray-200 transition-colors" data-id="element-1037" data-mp="1fqeulccosvra">
              Start 14-Day Trial
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-blue-900/20" data-id="element-1038" data-mp="frmk3wvwvnml">
        <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-1039" data-mp="kq788xxw2guo">
          Ready to boost your revenue?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1040" data-mp="opg4c9geiqte">
          Join thousands of merchants using FlowBoost to increase their average
          order value.
        </p>
        <button className="bg-white text-black px-10 py-5 rounded-full font-bold text-lg hover:scale-105 transition-transform" data-id="element-1041" data-mp="yodypt0idekb">
          Install FlowBoost Now
        </button>
      </section>
    </div>;
}